import React, { useEffect, useState } from "react";
import { FormikType } from "../../../../types/types";
import { InitialAuditionStateType } from "../../../../assets/data/initialState";
import { useIADispatch, useIASelector } from "../../../../redux/hooks";
import {
  addSelectedLocationTagFilter,
  locationTagFiltersSelector,
  selectedLocationTagFilterSelector,
  setLocationTagFilters,
  setSelectedLocationTagFilter,
} from "../../../../redux/slices/locationTagFiltersSlice";
import { useLazyFetchLocationTagFiltersQuery } from "../../../../services/LocationTagFiltersServices/LocationTagFiltersServices";
import { Primitives } from "../../../../assets/primitives";
import { toast } from "react-toastify";
import BasicLoader from "../../loader/BasicLoader";
import InfiniteScroll from "react-infinite-scroller";
import DataRowCard from "../../cards/DataRowCard";

type Props = FormikType<InitialAuditionStateType>;

export default function LocationTagFilterModalLocationTagFilters({
  formik,
}: Props) {
  const dispatch = useIADispatch();
  const locationTagFilters = useIASelector(locationTagFiltersSelector);
  const selectedLocationTagFilter = useIASelector(
    selectedLocationTagFilterSelector
  );

  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const [fetchLocationTagFiltersQuery, { isLoading, isFetching }] =
    useLazyFetchLocationTagFiltersQuery();

  useEffect(() => {
    setSkip(0);
    dispatch(setSelectedLocationTagFilter(null));
    fetchLocationTagFilters();
  }, []);

  const fetchLocationTagFilters = async () => {
    try {
      const response = await fetchLocationTagFiltersQuery(
        {
          search: "",
          skip: Primitives.IA_INITIAL_SKIP,
          take: Primitives.IA_TAKE,
        },
        true
      ).unwrap();

      if (response) {
        const filteredLocationTagFilters = response.filter(
          (l1) =>
            !formik.values.locationTagFilters.some((l2) => l1.id === l2.id)
        );
        dispatch(setLocationTagFilters(filteredLocationTagFilters));
      }

      if (response && response.length < Primitives.IA_TAKE) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const fetchMore = () => {
    if (!isLoadingMore && !isLoading) {
      fetchMoreLocationTagFilters();
    }
  };

  const fetchMoreLocationTagFilters = async () => {
    setIsLoadingMore(true);
    try {
      const response = await fetchLocationTagFiltersQuery(
        {
          search: "",
          skip: skip + Primitives.IA_TAKE,
          take: Primitives.IA_TAKE,
        },
        true
      ).unwrap();

      if (!response || !response.length) {
        setHasMore(false);
      } else {
        setHasMore(true);
        if (!locationTagFilters) return;
        const newLocationTagFilters = locationTagFilters.concat(response);
        dispatch(setLocationTagFilters(newLocationTagFilters));
      }

      setSkip(skip + Primitives.IA_TAKE);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderLocationTags = () => {
    if ((isLoading || isFetching) && !isLoadingMore)
      return (
        <div className="w-full">
          <BasicLoader color="black" />
        </div>
      );
    if (!locationTagFilters)
      return (
        <div className="w-full">
          <p className="font-Switzer text-base text-black">No Location Tags</p>
        </div>
      );

    return (
      <div className="w-full h-[calc(31vh)] overflow-y-auto">
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchMore}
          hasMore={hasMore}
          useWindow={false}
          loader={<BasicLoader color="black" className="py-1" />}
          initialLoad={true}
          threshold={50}
        >
          {locationTagFilters.map((lt, i) => (
            <DataRowCard
              key={i}
              text={lt.title}
              onClickContainer={() =>
                dispatch(setSelectedLocationTagFilter(lt))
              }
              onClickIcon={() => dispatch(addSelectedLocationTagFilter(lt))}
              isSelectedToUpdate={
                selectedLocationTagFilter
                  ? selectedLocationTagFilter.id === lt.id
                  : false
              }
            />
          ))}
        </InfiniteScroll>
      </div>
    );
  };

  return (
    <div className="">
      <p className="font-Switzer font-bold text-black text-2xl my-4">
        Location Tags
      </p>
      {renderLocationTags()}
    </div>
  );
}
